import {
  Button,
  FieldText,
  Modal,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import type { PhoneNumber } from 'lib-shared-legacy-types';
import { ChangeEvent, FC, useCallback, useEffect, useState } from 'react';

import type { FlowCallActionT } from 'components/powerCallFlow/api/types';
import type { WithFlowsInjectedPropsT } from 'hocs';
import type { FlowT } from 'hocs/flows/api/types';
import { validateName } from 'utils/formValidator';

import PrettyNumber from '../PrettyNumber';

import styles from './renameFlowModal.module.scss';

type PropsT = {
  isShown: boolean;
  selectedFlow: FlowT<FlowCallActionT> | null;
  number: PhoneNumber;
  onClose: () => void;
} & WithFlowsInjectedPropsT<FlowCallActionT>;

const RenameFlowModal: FC<PropsT> = ({
  isShown,
  selectedFlow,
  number,
  flows,
  onClose,
}) => {
  const { name = '' } = selectedFlow || {};
  const [isLoading, setIsLoading] = useState(false);
  const [value, setValue] = useState(name || '');

  useEffect(() => {
    setValue(name || '');
  }, [name]);

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement> | undefined) => {
      if (!event) {
        return;
      }

      const {
        target: { value },
      } = event;

      setValue(value);
    },
    [setValue],
  );

  const onSave = useCallback(() => {
    if (!selectedFlow) {
      return;
    }

    const { id } = selectedFlow;

    setIsLoading(true);

    flows
      .update(id, { ...selectedFlow, name: value })
      .then(() => setIsLoading(false))
      .then(() => onClose());
  }, [value, selectedFlow, flows, setIsLoading, onClose]);

  if (!selectedFlow) {
    return null;
  }

  return (
    <Modal isOpen={isShown} onClose={onClose}>
      <ModalHead title="Rename flow" onClose={onClose} />
      <ModalBody>
        <p className={styles.text}>
          You are about to rename the flow for number{' '}
          <strong>
            <PrettyNumber number={number} />
          </strong>
        </p>
        <FieldText
          meta={{
            error: validateName(value),
          }}
          input={{ value, name: 'name', onChange }}
          label="Name"
        />
      </ModalBody>
      <ModalFoot>
        <Button isLoading={isLoading} onClick={onSave}>
          Save
        </Button>
      </ModalFoot>
    </Modal>
  );
};

export default RenameFlowModal;
